import { REPORT_TRANSLATION_LANGUAGES } from '../../../constants/reportTranslationLanguages';
import { isRunningStatus } from '../../../constants/jobStatus';

export const SYNTHESIS_MODE = 'event_synthesis';
export const OFFICIAL_ID = 'official';
export const DEFAULT_LANGUAGE = 'en';

export const sessionIdOf = (session) => session?.id || session?.session_id || session?.sessionId;
export const sessionTitleOf = (session) => session?.title || session?.original_filename || 'Untitled session';

export const transcriptReadyOf = (session) => {
  if (session?.has_transcripts === true || Number(session?.transcript_count) > 0) return true;
  const status = String(session?.transcript_status || session?.transcriptStatus || session?.status || '').toUpperCase();
  return status === 'COMPLETED';
};

export const apiError = (err, fallback) => err.message || fallback;

export const listOrEmpty = (value) => (Array.isArray(value) ? value : []);

export const languageName = (code) => (
  REPORT_TRANSLATION_LANGUAGES.find((item) => item.code === code)?.name || code?.toUpperCase() || ''
);

const uploadDayOf = (session) => {
  const raw = session?.created_at || session?.createdAt;
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  // Local calendar day, so the split matches the dates shown everywhere else.
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${String(date.getDate()).padStart(2, '0')}`;
};

const dayHint = (key) => new Date(`${key}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

/**
 * Split sessions into the days they were uploaded on, oldest day first.
 * Sessions without a usable date end up in a trailing "No date" group.
 */
export const groupSessionsByUploadDay = (sessions) => {
  const buckets = new Map();
  sessions.forEach((session) => {
    const key = uploadDayOf(session);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(session);
  });

  const groups = [...buckets.keys()]
    .filter(Boolean)
    .sort()
    .map((key, index) => ({ key, label: `Day ${index + 1}`, hint: dayHint(key), sessions: buckets.get(key) }));

  if (buckets.has('')) groups.push({ key: 'undated', label: 'No date', hint: '', sessions: buckets.get('') });
  return groups;
};

const pageFlagLabel = (page) => {
  if (page?.mode === SYNTHESIS_MODE) return '';
  const country = page?.country || '';
  if (!page?.report_kind) return country;
  const official = page.is_official ? ' · official' : '';
  const kind = page.report_kind === 'daily' ? `Day ${page.day_number || '?'}` : 'Full event';
  return [kind + official, country].filter(Boolean).join(' · ');
};

export const statusNote = (page) => {
  if (!page) return '';
  if (isRunningStatus(page.status)) {
    return page.current_step ? `${page.current_step} (${Math.round(page.progress || 0)}%)` : 'Generating...';
  }
  if (page.status === 'COMPLETED' && page.has_output) return 'JSON ready';
  if (page.status === 'FAILED') return 'Failed';
  return 'Draft';
};

export const isRunning = (item) => isRunningStatus(item?.status);

/** The version dropdown: the official report first (when there is one), then every page. */
export const versionOptionsOf = (pages, hasOfficial, officialLabel) => [
  ...(hasOfficial ? [{ id: OFFICIAL_ID, label: officialLabel, status: null }] : []),
  ...pages.map((page) => {
    const name = page.title || 'Untitled report';
    const flag = pageFlagLabel(page);
    return { id: page.id, label: flag ? `${name} (${flag})` : name, status: page.status || 'DRAFT' };
  }),
];

/** The preselected sessions that have a finished transcript. */
export const readyDefaultIds = (defaultSelectedIds, readySessions) => {
  const readyIds = readySessions.map(sessionIdOf).filter(Boolean);
  return (defaultSelectedIds || []).filter((id) => readyIds.includes(id));
};

export const downloadBaseName = (reportTitle, lang) => (
  `${(reportTitle || 'custom_report').replace(/\s+/g, '_')}${lang ? `_${lang}` : ''}`
);

export const generationStartedMessage = (isSynthesis, started, sessionCount) => {
  if (isSynthesis) {
    return `Merging ${started.session_ids?.length || 'all'} session reports. This takes about 30 minutes.`;
  }
  return sessionCount === 1
    ? 'Running only your prompt on this transcript.'
    : `Running only your prompt across all ${sessionCount} transcripts as one analysis.`;
};

export const digwatchPublishedMessage = (isSynthesis, result) => {
  if (isSynthesis) {
    return `DigWatch is rendering the page in the background${result.published_url ? `: ${result.published_url}` : '.'}`;
  }
  return result.published_url
    ? `Page created. Event page should link to ${result.published_url}`
    : 'Sent to DigWatch. They still need to return a page URL.';
};

/** The collapsed editor header: the version name, plus the session count on an event. */
export const collapsedEditorHint = (reportTitle, scope, sessionCount) => (
  `${reportTitle || 'Untitled report'}${scope === 'event' ? ` · ${sessionCount} session${sessionCount === 1 ? '' : 's'}` : ''}`
);
